import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Eye, FileText, LayoutPanelLeft, Save, Star, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { AppShell } from '@/components/layout/AppShell';
import { AdminSectionTabs } from '@/components/admin/AdminSectionTabs';
import { ArticleThumbnailUploader } from '@/components/admin/ArticleThumbnailUploader';
import { Button } from '@/components/ui/Button';
import { ArticleBodyRenderer } from '@/components/learn/ArticleBodyRenderer';
import { QuizSidebarLink } from '@/components/admin/QuizSidebarLink';
import { adminArticlesService } from '@/services/admin-articles.service';
import { ApiError } from '@/services/http';
import { LEARNS_TOPICS, LEARNS_TONE_PALETTES, type LearnsTopicId } from '@/constants/learns-data';
import { READING_LEVELS, type ReadingLevel } from '@/store/learning.store';
import { cn } from '@/lib/cn';
import type { ArticleStatus, ArticleTone, ArticleView } from '@/types/api';

/**
 * AdminEditArticlePage — `/admin/articles/:id` for existing drafts and
 * `/admin/articles/new` for a blank slate.
 *
 * Layout:
 *   - Top bar: back link, title input (inline-edited), unsaved indicator,
 *     Save button, status toggle (Draft / Live), delete.
 *   - Main: two-column layout whose split is controlled by a three-way
 *     preview mode (edit / split / preview). Left side is the markdown
 *     textarea; right side is the live preview rendered by the shared
 *     MarkdownRenderer.
 *   - Sidebar (fixed 320px on the right): slug, summary, topic, level,
 *     tone, author, featured toggle.
 *
 * Unsaved state comes from a simple deep-equal check between `draft`
 * and the last-saved `source` snapshot; the Save button stays disabled
 * when there is nothing to save, and a beforeunload listener warns the
 * user if they try to leave with pending edits.
 */

type PreviewMode = 'edit' | 'split' | 'preview';

/** Fields the editor mutates locally. Mirrors UpdateArticlePayload but
 *  with concrete defaults so the controlled inputs never see undefined. */
interface ArticleDraft {
  title: string;
  slug: string;
  summary: string;
  body: string;
  topic: LearnsTopicId;
  level: ReadingLevel;
  tone: ArticleTone;
  mins: number;
  author: string;
  status: ArticleStatus;
  isFeatured: boolean;
}

const EMPTY_DRAFT: ArticleDraft = {
  title: '',
  slug: '',
  summary: '',
  body: '',
  topic: 'science',
  level: '5\u20136',
  tone: 'warm',
  mins: 0,
  author: '',
  status: 'pending',
  isFeatured: false,
};

function toDraft(article: ArticleView): ArticleDraft {
  return {
    title: article.title,
    slug: article.slug,
    summary: article.summary ?? '',
    body: article.body ?? '',
    topic: (article.topic as LearnsTopicId) ?? 'science',
    level: (article.level as ReadingLevel) ?? '5\u20136',
    tone: (article.tone as ArticleTone) ?? 'warm',
    mins: article.mins ?? 0,
    author: article.author ?? '',
    status: (article.status === 'live' ? 'live' : 'pending') as ArticleStatus,
    isFeatured: article.isFeatured,
  };
}

function draftsEqual(a: ArticleDraft, b: ArticleDraft): boolean {
  return (
    a.title === b.title &&
    a.slug === b.slug &&
    a.summary === b.summary &&
    a.body === b.body &&
    a.topic === b.topic &&
    a.level === b.level &&
    a.tone === b.tone &&
    a.mins === b.mins &&
    a.author === b.author &&
    a.status === b.status &&
    a.isFeatured === b.isFeatured
  );
}

export function AdminEditArticlePage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();

  const [source, setSource] = useState<ArticleDraft | null>(isNew ? EMPTY_DRAFT : null);
  const [draft, setDraft] = useState<ArticleDraft>(isNew ? EMPTY_DRAFT : EMPTY_DRAFT);
  const [articleId, setArticleId] = useState<string | null>(isNew ? null : id!);
  // Thumbnail lives outside the draft because it's mutated through its
  // own POST/DELETE endpoints (multipart), not through the article PATCH
  // body. We still update it here whenever the API hands us a fresh
  // ArticleView so the sidebar preview stays consistent.
  const [thumbnailImage, setThumbnailImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState<PreviewMode>('split');

  // Load existing article on mount.
  useEffect(() => {
    if (isNew || !id) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const row = await adminArticlesService.getById(id);
        if (cancelled) return;
        const next = toDraft(row);
        setSource(next);
        setDraft(next);
        setArticleId(row.id);
        setThumbnailImage(row.thumbnailImage);
      } catch (err) {
        if (cancelled) return;
        toast.error(err instanceof ApiError ? err.message : 'Could not load article.');
        navigate('/admin/articles', { replace: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isNew, navigate]);

  const dirty = useMemo(() => {
    if (!source) return false;
    return !draftsEqual(source, draft);
  }, [source, draft]);

  // Warn before navigating away with unsaved edits.
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const setField = <K extends keyof ArticleDraft>(key: K, value: ArticleDraft[K]) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (saving) return;
    if (!draft.title.trim()) {
      toast.error('Title is required.');
      return;
    }

    setSaving(true);
    try {
      if (isNew || !articleId) {
        const created = await adminArticlesService.create({
          title: draft.title,
          topic: draft.topic,
          level: draft.level,
          slug: draft.slug || undefined,
          summary: draft.summary || undefined,
          body: draft.body || undefined,
          tone: draft.tone,
          mins: draft.mins || undefined,
          author: draft.author.trim() ? draft.author : null,
          status: draft.status,
          isFeatured: draft.isFeatured,
        });
        toast.success('Article created.');
        const next = toDraft(created);
        setSource(next);
        setDraft(next);
        setArticleId(created.id);
        setThumbnailImage(created.thumbnailImage);
        navigate(`/admin/articles/${created.id}`, { replace: true });
      } else {
        const updated = await adminArticlesService.update(articleId, {
          title: draft.title,
          slug: draft.slug,
          summary: draft.summary,
          body: draft.body,
          topic: draft.topic,
          level: draft.level,
          tone: draft.tone,
          mins: draft.mins,
          author: draft.author.trim() ? draft.author : null,
          status: draft.status,
          isFeatured: draft.isFeatured,
        });
        toast.success('Saved.');
        const next = toDraft(updated);
        setSource(next);
        setDraft(next);
        setThumbnailImage(updated.thumbnailImage);
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!articleId || isNew) return;
    const ok = window.confirm(`Delete "${draft.title || 'this article'}"? This cannot be undone.`);
    if (!ok) return;
    try {
      await adminArticlesService.remove(articleId);
      toast.success('Deleted.');
      navigate('/admin/articles');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  if (loading) {
    return (
      <AppShell mode="parent">
        <div className="py-16 text-center text-or-slate-500">Loading…</div>
      </AppShell>
    );
  }

  return (
    <AppShell mode="parent">
      <div className="flex flex-col gap-6">
        <AdminSectionTabs />

        {/* Top bar */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/admin/articles')}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-or-slate-200 bg-white text-or-slate-600 hover:bg-or-slate-50"
              aria-label="Back to article list"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden />
            </button>
            <div>
              <div className="or-kicker text-or-orange-600">
                {isNew ? 'New article' : 'Edit article'}
              </div>
              <input
                type="text"
                value={draft.title}
                onChange={(e) => setField('title', e.target.value)}
                placeholder="Article title"
                className="mt-0.5 w-full min-w-[280px] bg-transparent font-display text-[22px] font-bold text-or-slate-900 outline-none placeholder:text-or-slate-400 sm:text-[26px]"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            {dirty ? (
              <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-or-orange-600">
                Unsaved changes
              </span>
            ) : !isNew ? (
              <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-or-slate-400">
                Saved
              </span>
            ) : null}
            {!isNew ? (
              <Button variant="danger" size="md" onClick={handleDelete}>
                <Trash2 className="h-4 w-4" aria-hidden />
                Delete
              </Button>
            ) : null}
            <Button
              variant="primary"
              size="md"
              onClick={handleSave}
              disabled={saving || (!dirty && !isNew)}
            >
              <Save className="h-4 w-4" aria-hidden />
              {saving ? 'Saving…' : isNew ? 'Create' : 'Save'}
            </Button>
          </div>
        </div>

        {/* Preview mode toggle */}
        <div className="flex items-center gap-2">
          <PreviewModeToggle mode={previewMode} onChange={setPreviewMode} />
          <span className="text-[12px] text-or-slate-500">
            HTML source. Paste AI-generated layouts + infographics; scripts and event handlers are
            stripped on render.
          </span>
        </div>

        {/* Main editor area */}
        <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
          <EditorPanel
            mode={previewMode}
            body={draft.body}
            onBodyChange={(value) => setField('body', value)}
          />
          <Sidebar
            draft={draft}
            setField={setField}
            isNew={isNew}
            articleId={articleId}
            thumbnailImage={thumbnailImage}
            onThumbnailChange={setThumbnailImage}
          />
        </div>
      </div>
    </AppShell>
  );
}

// -- Sub-components ------------------------------------------------------

function PreviewModeToggle({
  mode,
  onChange,
}: {
  mode: PreviewMode;
  onChange: (next: PreviewMode) => void;
}) {
  const options: Array<{ value: PreviewMode; label: string; icon: React.ReactNode }> = [
    { value: 'edit', label: 'Edit', icon: <FileText className="h-3.5 w-3.5" aria-hidden /> },
    {
      value: 'split',
      label: 'Split',
      icon: <LayoutPanelLeft className="h-3.5 w-3.5" aria-hidden />,
    },
    { value: 'preview', label: 'Preview', icon: <Eye className="h-3.5 w-3.5" aria-hidden /> },
  ];
  return (
    <div
      role="radiogroup"
      aria-label="Preview mode"
      className="inline-flex items-center rounded-full border border-or-slate-200 bg-white p-1"
    >
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          role="radio"
          aria-checked={mode === opt.value}
          onClick={() => onChange(opt.value)}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[12px] font-semibold transition-colors',
            mode === opt.value
              ? 'bg-or-slate-900 text-white'
              : 'text-or-slate-600 hover:bg-or-slate-100',
          )}
        >
          {opt.icon}
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function EditorPanel({
  mode,
  body,
  onBodyChange,
}: {
  mode: PreviewMode;
  body: string;
  onBodyChange: (next: string) => void;
}) {
  const showEditor = mode !== 'preview';
  const showPreview = mode !== 'edit';

  return (
    <div className={cn('grid gap-4', mode === 'split' ? 'lg:grid-cols-2' : 'grid-cols-1')}>
      {showEditor ? (
        <div className="flex flex-col">
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-or-slate-500">
            HTML source
          </div>
          <textarea
            value={body}
            onChange={(e) => onBodyChange(e.target.value)}
            placeholder="Paste HTML here — &lt;p&gt;, &lt;h2&gt;, &lt;svg&gt;, inline styles all welcome."
            spellCheck={false}
            className="min-h-[520px] w-full resize-y rounded-xl border border-or-slate-200 bg-white p-4 font-mono text-[13px] leading-relaxed text-or-slate-900 focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-400/30"
          />
        </div>
      ) : null}
      {showPreview ? (
        <div className="flex flex-col">
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-or-slate-500">
            Preview
          </div>
          <div className="min-h-[520px] overflow-auto rounded-xl border border-or-slate-200 bg-white p-6">
            {body.trim() ? (
              <ArticleBodyRenderer html={body} />
            ) : (
              <p className="text-[13px] italic text-or-slate-400">
                Paste HTML on the left to see a preview.
              </p>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Sidebar({
  draft,
  setField,
  isNew,
  articleId,
  thumbnailImage,
  onThumbnailChange,
}: {
  draft: ArticleDraft;
  setField: <K extends keyof ArticleDraft>(key: K, value: ArticleDraft[K]) => void;
  isNew: boolean;
  articleId: string | null;
  thumbnailImage: string | null;
  onThumbnailChange: (next: string | null) => void;
}) {
  return (
    <aside className="flex flex-col gap-4 rounded-2xl border border-or-slate-200 bg-white p-5">
      {/* Quiz link — only meaningful once the article is persisted. */}
      {!isNew && articleId ? <QuizSidebarLink parentKind="article" parentId={articleId} /> : null}

      {/* Thumbnail / header image — uploader stays mounted in the "save
          first" hint state when articleId is null so admins discover the
          control before their first save. */}
      <ArticleThumbnailUploader
        articleId={articleId}
        thumbnailImage={thumbnailImage}
        articleTitle={draft.title}
        onArticleChange={(article) => onThumbnailChange(article.thumbnailImage)}
      />

      {/* Status */}
      <Field label="Status">
        <div className="inline-flex rounded-full border border-or-slate-200 bg-or-slate-50 p-1">
          <StatusOption
            active={draft.status === 'pending'}
            onClick={() => setField('status', 'pending')}
            label="Pending"
          />
          <StatusOption
            active={draft.status === 'live'}
            onClick={() => setField('status', 'live')}
            label="Live"
          />
        </div>
      </Field>

      {/* Featured */}
      <Field label="Featured">
        <label className="inline-flex cursor-pointer items-center gap-2 text-[13px] text-or-slate-700">
          <input
            type="checkbox"
            checked={draft.isFeatured}
            onChange={(e) => setField('isFeatured', e.target.checked)}
            className="h-4 w-4 rounded border-or-slate-300 text-or-orange-600"
          />
          <Star
            className={cn(
              'h-3.5 w-3.5',
              draft.isFeatured ? 'text-or-yellow-400' : 'text-or-slate-300',
            )}
            fill={draft.isFeatured ? 'currentColor' : 'none'}
            aria-hidden
          />
          Promote to the Learns hero
        </label>
        <p className="text-[11px] text-or-slate-500">
          Only one article can be featured at a time. Saving will demote any other featured article.
        </p>
      </Field>

      {/* Slug */}
      <Field
        label="Slug"
        hint={
          isNew && !draft.slug
            ? 'Leave blank to auto-derive from the title.'
            : 'Used in the public URL (/learns/articles/<slug>).'
        }
      >
        <input
          type="text"
          value={draft.slug}
          onChange={(e) => setField('slug', e.target.value.toLowerCase())}
          placeholder={isNew ? 'auto-generated from title' : ''}
          className="h-9 w-full rounded-lg border border-or-slate-200 bg-white px-3 text-[13px] focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-400/30"
        />
      </Field>

      {/* Summary */}
      <Field label="Summary" hint="Shown on article cards and meta.">
        <textarea
          value={draft.summary}
          onChange={(e) => setField('summary', e.target.value)}
          rows={3}
          placeholder="1–2 sentences, curiosity-first."
          className="w-full resize-y rounded-lg border border-or-slate-200 bg-white px-3 py-2 text-[13px] focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-400/30"
        />
      </Field>

      {/* Topic */}
      <Field label="Topic">
        <select
          value={draft.topic}
          onChange={(e) => setField('topic', e.target.value as LearnsTopicId)}
          className="h-9 w-full rounded-lg border border-or-slate-200 bg-white px-3 text-[13px] font-semibold focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-400/30"
        >
          {LEARNS_TOPICS.map((t) => (
            <option key={t.id} value={t.id}>
              {t.label}
            </option>
          ))}
        </select>
      </Field>

      {/* Level */}
      <Field label="Reading level">
        <select
          value={draft.level}
          onChange={(e) => setField('level', e.target.value as ReadingLevel)}
          className="h-9 w-full rounded-lg border border-or-slate-200 bg-white px-3 text-[13px] font-semibold focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-400/30"
        >
          {READING_LEVELS.map((l) => (
            <option key={l} value={l}>
              Grade {l}
            </option>
          ))}
        </select>
      </Field>

      {/* Tone */}
      <Field label="Tone" hint="Stripe palette on article cards.">
        <div className="grid grid-cols-4 gap-2">
          {(Object.keys(LEARNS_TONE_PALETTES) as ArticleTone[]).map((tone) => (
            <button
              key={tone}
              type="button"
              onClick={() => setField('tone', tone)}
              aria-pressed={draft.tone === tone}
              className={cn(
                'flex h-10 items-end justify-center overflow-hidden rounded-lg border text-[11px] font-semibold capitalize',
                draft.tone === tone
                  ? 'border-or-slate-900 text-or-slate-900'
                  : 'border-or-slate-200 text-or-slate-600',
              )}
              style={{
                backgroundImage: `repeating-linear-gradient(135deg, ${LEARNS_TONE_PALETTES[tone].a} 0 8px, ${LEARNS_TONE_PALETTES[tone].b} 8px 16px)`,
              }}
            >
              <span
                className="mb-0.5 rounded-full bg-white/80 px-1.5 py-0.5"
                style={{ fontSize: '10px' }}
              >
                {tone}
              </span>
            </button>
          ))}
        </div>
      </Field>

      {/* Read time */}
      <Field
        label="Read time (mins)"
        hint="Auto-computed from the body on save. Set manually to override."
      >
        <input
          type="number"
          min={0}
          max={120}
          value={draft.mins}
          onChange={(e) => setField('mins', Math.max(0, Number(e.target.value) || 0))}
          className="h-9 w-24 rounded-lg border border-or-slate-200 bg-white px-3 text-[13px] focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-400/30"
        />
      </Field>

      {/* Author */}
      <Field label="Author (byline)">
        <input
          type="text"
          value={draft.author}
          onChange={(e) => setField('author', e.target.value)}
          placeholder="Optional — e.g. The Ollie Learns Desk"
          className="h-9 w-full rounded-lg border border-or-slate-200 bg-white px-3 text-[13px] focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-400/30"
        />
      </Field>
    </aside>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-or-slate-500">
        {label}
      </div>
      {children}
      {hint ? <p className="text-[11px] text-or-slate-500">{hint}</p> : null}
    </div>
  );
}

function StatusOption({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'rounded-full px-3 py-1 text-[12px] font-semibold transition-colors',
        active ? 'bg-white text-or-slate-900 shadow-or-card' : 'text-or-slate-500',
      )}
    >
      {label}
    </button>
  );
}
